import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Card,
  CardBody,
  CardTitle,
  Badge,
  Row,
  Col,
  ListGroup,
  ListGroupItem
} from "reactstrap";
import { format, parseISO } from 'date-fns';
import { ar } from 'date-fns/locale';

const TransactionModal = ({ isOpen, toggleModal, transaction }) => {
  if (!transaction || !transaction.booking) {
    return null;
  }

  const formatDate = (date) => {
    if (!date) return 'غير متوفر';
    
    try {
      let parsedDate;
      
      if (typeof date === 'object' && 'seconds' in date) {
        parsedDate = new Date(date.seconds * 1000);
      } else if (typeof date === 'string') {
        parsedDate = parseISO(date);
      } else if (date instanceof Date) {
        parsedDate = date;
      } else {
        return 'تاريخ غير صالح';
      }
      
      return format(parsedDate, 'yyyy/MM/dd - hh:mm a', { locale: ar });
    } catch (error) {
      console.error('خطأ في تحويل التاريخ:', error);
      return 'تاريخ غير صالح';
    }
  };

  const PaymentStatusBadge = ({ status }) => {
    const statusMap = {
      pending: { color: 'warning', text: 'قيد الانتظار', icon: 'bx bx-time' },
      paid: { color: 'success', text: 'تم الدفع', icon: 'bx bx-check' },
      failed: { color: 'danger', text: 'فشل الدفع', icon: 'bx bx-x' },
      approved: { color: 'primary', text: 'تمت الموافقة', icon: 'bx bx-check-circle' },
      rejected: { color: 'danger', text: 'مرفوض', icon: 'bx bx-x-circle' },
      completed: { color: 'info', text: 'مكتمل', icon: 'bx bx-check-double' }
    };

    const currentStatus = statusMap[status?.toLowerCase()] || { color: 'secondary', text: status, icon: 'bx bx-question-mark' };

    return (
      <Badge color={currentStatus.color} className="p-2">
        <i className={`${currentStatus.icon} align-middle me-1`} />
        {currentStatus.text}
      </Badge>
    );
  };

  const renderInfoItem = (label, value, isRTL = false) => (
    <ListGroupItem className="d-flex justify-content-between align-items-center">
      <span className="fw-bold">{label}</span>
      <span style={{ direction: isRTL ? "ltr" : "rtl" }}>
        {typeof value === 'object' ? value : (value || 'غير متوفر')}
      </span>
    </ListGroupItem>
  );

  return (
    <Modal isOpen={isOpen} toggle={toggleModal} size="lg" centered>
      <ModalHeader toggle={toggleModal} className="border-0 pb-0">
        <h4 className="modal-title">تفاصيل المعاملة</h4>
        <small className="text-muted">رقم المرجع: {transaction.id?.slice(0, 8) || 'N/A'}</small>
      </ModalHeader>
      
      <ModalBody>
        <Row>
          <Col md={6}>
            <Card className="mb-3">
              <CardBody>
                <CardTitle tag="h5" className="border-bottom pb-2 mb-3">
                  <i className="bx bx-user-circle me-2"></i>
                  معلومات العميل
                </CardTitle>
                <ListGroup flush>
                  {renderInfoItem("اسم العميل", transaction.app_user_id?.username)}
                  {renderInfoItem("رقم الجوال", transaction.app_user_id?.mobile, true)}
                </ListGroup>
              </CardBody>
            </Card>

            <Card className="mb-3">
              <CardBody>
                <CardTitle tag="h5" className="border-bottom pb-2 mb-3">
                  <i className="bx bx-calendar me-2"></i>
                  تفاصيل الحجز
                </CardTitle>
                <ListGroup flush>
                  {renderInfoItem("تاريخ العملية", formatDate(transaction.booking?.create_at))}
                  {renderInfoItem("حالة الحجز", <PaymentStatusBadge status={transaction.booking?.booking_status} />)}
                  {renderInfoItem("طريقة الدفع", transaction.booking?.paymentMethod)}
                  {renderInfoItem("حالة الدفع", <PaymentStatusBadge status={transaction.booking?.paymentStatus} />)}
                </ListGroup>
              </CardBody>
            </Card>
          </Col>

          <Col md={6}>
            <Card className="mb-3">
              <CardBody>
                <CardTitle tag="h5" className="border-bottom pb-2 mb-3">
                  <i className="bx bx-store me-2"></i>
                  معلومات مقدم الخدمة
                </CardTitle>
                <ListGroup flush>
                  {renderInfoItem("اسم الصالون", transaction.serviceProviders?.salon_name)}
                  {renderInfoItem("اسم المقدم", transaction.serviceProviders?.full_name)}
                  {renderInfoItem("رقم الجوال", transaction.serviceProviders?.mobile, true)}
                </ListGroup>
              </CardBody>
            </Card>

            <Card className="mb-3">
              <CardBody>
                <CardTitle tag="h5" className="border-bottom pb-2 mb-3">
                  <i className="bx bx-dollar-circle me-2"></i>
                  التفاصيل المالية
                </CardTitle>
                <ListGroup flush>
                  {renderInfoItem("الخدمة", transaction.serviceProviderServices?.name)}
                  {renderInfoItem("القسم", transaction.booking?.category_type)}
                  {renderInfoItem("الإجمالي", `${transaction.booking?.total ?? 0} ريال`)}
                  {renderInfoItem("نسبة رسوم التطبيق", `${transaction.commission_rates ?? 0}%`)}
                  {renderInfoItem("الإجمالي بعد خصم نسبة التطبيق", `${transaction.booking?.total_after_discount ?? 0} ريال`)}
                </ListGroup>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </ModalBody>

      <ModalFooter className="border-0">
        <Button color="secondary" onClick={toggleModal} className="me-2">
          إغلاق
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default TransactionModal;